// The salon's reviews (the same list the salon profile shows), each one a
// post ready to share word for word
import { reviewService } from '../../../../../_services/review.service'
import { makePost } from './postTypes'
import { usedSources } from './store'

const firstName = (s) => String(s || '').trim().split(/\s+/)[0] || ''

const shape = (r) => {
  const google = r.source === 'google'
  const author = google
    ? (r.authorName || 'Google review')
    : ((r.userId && (r.userId.name || [r.userId.first_name, r.userId.last_name].filter(Boolean).join(' '))) || r.clientName || 'A client')
  const first = (r.services || [])[0]
  // The stylist the review is for, when the server says (a self-employed
  // stylist's plans only use their own)
  const staff = r.hairdresserId || r.staffId || r.stylistId || null
  return {
    id: r._id,
    text: String(r.reviewText || r.comment || r.review || '').trim(),
    author,
    firstName: firstName(author),
    service: typeof first === 'string' ? first : ((first && first.serviceName) || ''),
    rating: Math.max(1, Math.min(5, Number(r.rating) || 5)),
    at: r.createdAt || null,
    google,
    staffId: (staff && (staff._id || staff.id)) || (typeof staff === 'string' ? staff : null),
  }
}

// A page of reviews, newest first as the API sends them
export const loadReviews = async ({ skip = 0, limit = 20 } = {}) => {
  const resp = await reviewService.getSalonReviews(localStorage.getItem('salonId'), skip, limit)
  const data = (resp && (resp.responseData || resp.data || resp)) || {}
  const raw = Array.isArray(data.reviews) ? data.reviews : []
  const reviews = raw.map(shape).filter(r => r.id && r.text)
  const pagination = data.pagination || {}
  return {
    reviews,
    next: skip + raw.length,
    hasMore: pagination.hasMore != null ? !!pagination.hasMore : raw.length >= limit,
    total: Number(pagination.total || (data.summary && data.summary.totalReviews)) || null,
    average: Number(data.summary && data.summary.averageRating) || null,
  }
}

// ─── Every review, kept for the visit ───
// The Social page, the studio's picker and the full list share one copy, so
// each opens straight away the next time. The API has no search or star
// filter (docs/SOCIAL_BACKEND.md), so the full list loads the rest a page at
// a time and searches on the device.
const FIRST = 20
const PAGE = 50
export const REVIEWS_CAP = 2000
const FRESH_MS = 5 * 60 * 1000

const salonNow = () => { try { return localStorage.getItem('salonId') } catch (e) { return null } }
// older: null, 'loading' or 'failed' (the pages after the first)
const blank = (salonId) => ({ salonId, list: [], next: 0, hasMore: false, total: null, average: null, loading: false, failed: false, older: null, at: 0 })
let kept = blank(null)
const listeners = new Set()
const put = (patch) => { kept = { ...kept, ...patch }; listeners.forEach(fn => fn(kept)) }

// A new review on top pushes the last one into the next page as well
const merge = (list, more) => {
  const seen = new Set(list.map(r => r.id))
  return list.concat(more.filter((r) => {
    if (seen.has(r.id)) return false
    seen.add(r.id)
    return true
  }))
}

export const salonReviews = () => (kept.salonId === salonNow() ? kept : blank(salonNow()))
export const subscribeSalonReviews = (fn) => { listeners.add(fn); return () => { listeners.delete(fn) } }

// The first page, unless this salon's is here and fresh
let firstLoad = null
export const ensureSalonReviews = () => {
  const salonId = salonNow()
  if (kept.salonId !== salonId) { kept = blank(salonId); firstLoad = null }
  else if (kept.at && Date.now() - kept.at < FRESH_MS) return Promise.resolve(kept)
  if (firstLoad) return firstLoad
  put({ loading: true, failed: false })
  const load = loadReviews({ limit: FIRST }).then((r) => {
    if (kept.salonId === salonId) put({ list: r.reviews, next: r.next, hasMore: r.hasMore, total: r.total, average: r.average, loading: false, failed: false, older: null, at: Date.now() })
  }, () => {
    if (kept.salonId === salonId) put({ loading: false, failed: true })
  }).then(() => {
    if (firstLoad === load) firstLoad = null
    return kept
  })
  firstLoad = load
  return load
}

// Everything after the first page, a page at a time, up to the cap
let restLoad = null // { salonId, load }
export const loadAllSalonReviews = () => {
  if (restLoad && restLoad.salonId === salonNow()) return restLoad.load
  const step = () => {
    const { salonId, next: from } = kept
    if (salonId !== salonNow() || kept.failed || !kept.hasMore || kept.list.length >= REVIEWS_CAP) return kept
    put({ older: 'loading' })
    return loadReviews({ skip: from, limit: PAGE }).then((r) => {
      if (kept.salonId !== salonId) return kept
      put({ list: merge(kept.list, r.reviews), next: r.next, hasMore: r.hasMore && r.next > from, total: r.total || kept.total, average: r.average || kept.average, older: null })
      return step()
    }, () => {
      if (kept.salonId === salonId) put({ older: 'failed' })
      return kept
    })
  }
  const load = ensureSalonReviews().then(step).then((s) => {
    if (restLoad && restLoad.load === load) restLoad = null
    return s
  })
  restLoad = { salonId: salonNow(), load }
  return load
}

// Accents and capitals don't matter: "zoe" finds Zoë
const fold = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
export const searchWords = (query) => fold(query).split(/\s+/).filter(Boolean)
const hays = new WeakMap()
const hayOf = (r) => {
  if (!hays.has(r)) hays.set(r, fold(`${r.author} ${r.text} ${r.service}`))
  return hays.get(r)
}

// The reviews with these stars (0 for any) and every word typed somewhere in
// their name, words or service
export const matchReviews = (list, { stars = 0, query = '' } = {}) => {
  const words = searchWords(query)
  return list.filter(r => (!stars || r.rating === stars) && words.every(w => hayOf(r).includes(w)))
}

// Where the words typed are in a piece of text, as [start, end] pairs, for
// highlighting. None when folding changed its length (the places would be off)
export const matchSpans = (text, words) => {
  const hay = fold(text)
  if (!words.length || hay.length !== String(text || '').length) return []
  const spans = []
  words.forEach((w) => {
    for (let i = hay.indexOf(w); i !== -1; i = hay.indexOf(w, i + w.length)) spans.push([i, i + w.length])
  })
  return spans.sort((a, b) => a[0] - b[0]).reduce((out, s) => {
    const last = out[out.length - 1]
    if (last && s[0] <= last[1]) last[1] = Math.max(last[1], s[1])
    else out.push([s[0], s[1]])
    return out
  }, [])
}

// Reviews worth sharing: kind and long enough to say something
export const shareable = (r) => r.rating >= 4 && r.text.length >= 20
export const isShared = (r) => !!usedSources()[`review:${r.id}`]

// Each review drawn in a different design, so the row shows the range: the
// review layouts and the three styles, in turn
const LOOKS = [
  ['layouts', 'paper', 'quote'], ['poster', 'red'], ['layouts', 'rose', 'conversation'], ['layouts', 'electric', 'receipt'],
  ['editorial', 'blush'], ['layouts', 'sage', 'note'], ['quiet', 'rose'], ['layouts', 'electric', 'rating'],
]
export const reviewPost = (r, { trade = 'hair', index = 0, brandColour } = {}) => {
  const [family, colourway, layout] = LOOKS[index % LOOKS.length]
  return makePost('review', {
    trade,
    family,
    layout,
    colourway,
    brandColour,
    content: { quote: r.text, name: r.firstName || 'A client', service: r.service || '', rating: r.rating },
    source: `review:${r.id}`,
  })
}

// "12 Sep" or "3 days ago" for a review's date
export const reviewWhen = (iso, now = new Date()) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const days = Math.floor((now - d) / 86400000)
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) })
}
